'use client';

import { useState, useRef, useCallback } from 'react';
import { useUAMPContext } from '@/components/uamp-provider';
import type { ClientEvent, ServerEvent } from '@/lib/uamp';
import type { UAMPClientEventMap } from '@/lib/uamp/client';

type UAMPEventName = keyof UAMPClientEventMap;

export function useUAMP() {
  const { client, isConnected } = useUAMPContext();
  const [connectionError] = useState<string | null>(null);
  const clientRef = useRef(client);

  clientRef.current = client;

  const joinChat = useCallback((chatId: string) => {
    clientRef.current?.joinChat(chatId);
  }, []);

  const leaveChat = useCallback((chatId: string) => {
    clientRef.current?.leaveChat(chatId);
  }, []);

  const sendTyping = useCallback((chatId: string, isTyping: boolean) => {
    clientRef.current?.sendTyping(chatId, isTyping);
  }, []);

  const markRead = useCallback((chatId: string) => {
    clientRef.current?.markRead(chatId);
  }, []);

  const on = useCallback(<K extends UAMPEventName>(
    event: K,
    callback: UAMPClientEventMap[K]
  ) => {
    const c = clientRef.current;
    if (!c) return () => {};
    return c.on(event, callback);
  }, []);

  const onUAMPEvent = useCallback((callback: (event: ServerEvent) => void) => {
    const c = clientRef.current;
    if (!c) return () => {};
    return c.on('uamp:event', callback);
  }, []);

  const sendAudio = useCallback((chatId: string, base64: string, format?: string, isFinal?: boolean) => {
    clientRef.current?.sendAudio(chatId, base64, format, isFinal);
  }, []);

  const sendVoiceInvite = useCallback((chatId: string) => {
    clientRef.current?.sendVoiceInvite(chatId);
  }, []);

  const sendVoiceAccept = useCallback((chatId: string) => {
    clientRef.current?.sendVoiceAccept(chatId);
  }, []);

  const sendVoiceEnd = useCallback((chatId: string) => {
    clientRef.current?.sendVoiceEnd(chatId);
  }, []);

  const sendCancel = useCallback((chatId: string, responseId?: string) => {
    clientRef.current?.sendCancel(chatId, responseId);
  }, []);

  return {
    isConnected,
    connectionError,
    joinChat,
    leaveChat,
    sendTyping,
    markRead,
    on,
    onUAMPEvent,
    sendAudio,
    sendVoiceInvite,
    sendVoiceAccept,
    sendVoiceEnd,
    sendCancel,
  };
}
